import React, { useEffect, useId, useRef, useState } from "react";
import { Check, Copy, PenLine } from "lucide-react";

import "../styles/learning-match.css";
import "../styles/learning-context.css";
import { SESSION_CONTEXT_TEXT, fullWhen } from "./catalogText";

const T = SESSION_CONTEXT_TEXT;
const M = T.manual;
const ICON_SIZE = 12;
const SOURCE_ICON_SIZE = 14;
const QUOTE_CLAMP_CHARS = 280;   // a longer first query starts collapsed (four lines) with Show more
const COPY_RESET_MS = 2000;
const SKELETON_CELLS = 4;
const COPY = Object.freeze({ IDLE: "idle", DONE: "done", FAILED: "failed" });

// context keys the grid shows as cells (the server's labels); every other entry is listed under the grid
const GRID_KEY = Object.freeze({ PRODUCT: "product", CATEGORY: "category", OUTCOME: "outcome" });
const GRID_KEYS = new Set(Object.values(GRID_KEY));

/** The grid's cells from a loaded session context, in reading order, and the entries left for the list under it. */
export function contextCells(data) {
  const by = new Map((data.entries || []).map((e) => [e.key, e]));
  const entryCell = (key, chip = false) => {
    const e = by.get(key);
    return e && !Array.isArray(e.value) ? { key, label: e.label, value: e.value, chip } : null;
  };
  const turns = data.diagnosis && data.diagnosis.turns;
  const started = data.firstQuery && data.firstQuery.at;
  const outcome = entryCell(GRID_KEY.OUTCOME, true);
  const cells = [
    entryCell(GRID_KEY.PRODUCT),
    entryCell(GRID_KEY.CATEGORY),
    data.startedBy && { key: "engineer", label: T.grid.engineer, value: data.startedBy },
    started && { key: "started", label: T.grid.started, value: fullWhen(started) },
    typeof turns === "number" && turns > 0 && { key: "messages", label: T.grid.messages, value: String(turns) },
    outcome && { ...outcome, label: T.grid.status },
  ].filter(Boolean);
  const rest = (data.entries || []).filter((e) => !GRID_KEYS.has(e.key) || Array.isArray(e.value));
  return { cells, rest };
}

/** Compact label / value cells; a `chip` value shows as a status tag, `code` as an identifier. */
export function ContextGrid({ cells, label }) {
  return (
    <dl className="le-cgrid" aria-label={label}>
      {cells.map((c) => (
        <div key={c.key} className="le-cgrid-cell">
          <dt>{c.label}</dt>
          <dd>{c.chip ? <span className="le-tag">{c.value}</span> : c.code ? <code>{c.value}</code> : c.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** The context entries the grid does not hold (call context, conversation title, facts): label / value, lists as lists. */
export function ContextList({ entries }) {
  return (
    <dl className="le-defs le-cdetails">
      {entries.map((e) => (
        <React.Fragment key={e.key}>
          <dt>{e.label}</dt>
          <dd>{Array.isArray(e.value) ? <ul className="le-plain">{e.value.map((v, i) => <li key={i}>{v}</li>)}</ul> : e.value}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

function CopyButton({ text }) {
  const [state, setState] = useState(COPY.IDLE);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const settle = (next) => {
    setState(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setState(COPY.IDLE), COPY_RESET_MS);
  };
  const copy = async () => {
    try {
      if (!navigator.clipboard || typeof navigator.clipboard.writeText !== "function") throw new Error("clipboard_unavailable");
      await navigator.clipboard.writeText(text);
      settle(COPY.DONE);
    } catch {
      settle(COPY.FAILED);
    }
  };
  const said = state === COPY.DONE ? T.copied : state === COPY.FAILED ? T.copyFailed : "";
  return (
    <>
      <button type="button" className="le-btn le-btn--sm" aria-label={T.copyLabel} onClick={copy}>
        {state === COPY.DONE ? <Check size={ICON_SIZE} aria-hidden="true" /> : <Copy size={ICON_SIZE} aria-hidden="true" />}
        {said || T.copy}
      </button>
      <span className="sr-only" aria-live="polite">{said}</span>
    </>
  );
}

/** The engineer's first message as a quote: long text collapsed with Show more, a caption, and Copy. */
export function FirstQueryQuote({ text }) {
  const id = useId();
  const long = text.length > QUOTE_CLAMP_CHARS;
  const [expanded, setExpanded] = useState(false);
  return (
    <figure className="le-cquote">
      <blockquote id={`${id}-q`} className={`le-cquote-text${long && !expanded ? " is-clamped" : ""}`}>{text}</blockquote>
      <figcaption className="le-cquote-foot">
        <span className="le-cquote-caption">{T.firstQueryCaption}</span>
        <span className="le-spacer" />
        {long && (
          <button type="button" className="le-btn le-btn--sm" aria-expanded={expanded} aria-controls={`${id}-q`}
            onClick={() => setExpanded((v) => !v)}>{expanded ? T.showLess : T.showMore}</button>
        )}
        <CopyButton text={text} />
      </figcaption>
    </figure>
  );
}

/** A Manual entry's source in place of a chat: who saved it, when, its category and the case id. */
export function ManualSource({ manual }) {
  const id = useId();
  const cells = [
    { key: "savedBy", label: M.savedBy, value: manual.savedBy || M.unknown },
    manual.savedAt && { key: "savedAt", label: M.savedAt, value: fullWhen(manual.savedAt) },
    manual.category && { key: "category", label: M.category, value: manual.category },
    manual.caseId != null && { key: "caseId", label: M.caseId, value: String(manual.caseId), code: true },
  ].filter(Boolean);
  return (
    <div className="le-csource" role="group" aria-labelledby={`${id}-h`}>
      <div className="le-csource-head">
        <PenLine size={SOURCE_ICON_SIZE} aria-hidden="true" />
        <span className="le-label" id={`${id}-h`}>{M.title}</span>
      </div>
      {manual.rootCause && (
        <p className="le-csource-cause"><span className="le-muted">{M.rootCause}</span> <strong>{manual.rootCause}</strong></p>
      )}
      <p className="le-muted">{M.hint}</p>
      <ContextGrid cells={cells} label={M.title} />
    </div>
  );
}

export const QuoteSkeleton = () => (
  <div className="le-cquote le-cquote--skel" aria-hidden="true">
    <span className="le-stat-skel le-cquote-skel--long" />
    <span className="le-stat-skel le-cquote-skel--short" />
    <span className="le-stat-skel le-cquote-skel--caption" />
  </div>
);

export const GridSkeleton = ({ count = SKELETON_CELLS }) => (
  <div className="le-cgrid" aria-hidden="true">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="le-cgrid-cell le-cgrid-cell--skel">
        <span className="le-stat-skel le-stat-skel--label" />
        <span className="le-stat-skel le-stat-skel--value" />
      </div>
    ))}
  </div>
);
